// src/apollo/client.ts
import {
  ApolloClient,
  InMemoryCache,
  createHttpLink,
  ApolloLink,
  Observable,
} from '@apollo/client/core';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import keycloak from '@/auth/keycloak';

// Create an HTTP link to your GraphQL endpoint
export const GRAPHQL_ENDPOINT_URL =
  process.env.VUE_APP_API_BASE_URL || 'http://localhost:8000/graphql';

const httpLink = createHttpLink({
  uri: GRAPHQL_ENDPOINT_URL,
});

// attach Authorization for every request using the current KC token
const authLink = setContext((_, { headers }) => {
  const token = keycloak.token;
  return {
    headers: {
      ...headers,
      Authorization: token ? `Bearer ${token}` : '',
    },
  };
});

let refreshPromise: Promise<boolean> | null = null;

const errorLink = onError(({ networkError, operation, forward }) => {
  const status =
    (networkError as any)?.statusCode ??
    (networkError as any)?.status ??
    (networkError as any)?.response?.status;

  if (status !== 401) return;

  console.log('Received 401 error, attempting token refresh...');

  // If there's no ongoing refresh, start one
  if (!refreshPromise) {
    console.debug('Starting token refresh...');
    refreshPromise = keycloak
      .updateToken(0) // try to refresh immediately
      .then(() => {
        console.debug('Token refresh successful');
        return true;
      })
      .catch(() => {
        console.debug('Token refresh failed');
        return false;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }

  // Return an Observable that waits for refresh, then retries the op once
  return new Observable((observer) => {
    refreshPromise!
      .then((ok) => {
        if (!ok) {
          // refresh failed -> bounce to login and surface the error
          console.debug('Token refresh failed, redirecting to login...');
          keycloak.login();
          observer.error(networkError);
          return;
        }
        // retry with the new token
        console.debug('Token refreshed, retrying GraphQL operation...');
        const sub = forward(operation).subscribe({
          next: (v) => observer.next(v),
          error: (e) => observer.error(e),
          complete: () => observer.complete(),
        });
        // teardown
        console.debug('Tearing down retry subscription');
        return () => sub.unsubscribe();
      })
      .catch((e) => observer.error(e));
  });
});

// Initialize Apollo Cache and default data
// const cache = new InMemoryCache();
const cache = new InMemoryCache({
  typePolicies: {
    ComputeTaskType: {
      fields: {
        subtasks: {
          keyArgs: ['types'], // cache a distinct list per types argument
          merge(_existing, incoming) {
            return incoming;
          }, // simple replace
        },
      },
    },
  },
});

// Instantiate Apollo Client
const apolloClient = new ApolloClient({
  link: ApolloLink.from([errorLink, authLink, httpLink]), // order matters
  cache: cache,
  connectToDevTools: true,
  devtools: { enabled: true },
});

export default apolloClient;
